import jsPDF from 'jspdf';
import { letterTemplates } from './letterTemplates';

export const generatePDF = async (formData) => {
  const pdf = new jsPDF('p', 'pt', 'letter');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 54; // 0.75 inch margins
  const contentWidth = pageWidth - (margin * 2);
  
  let yPosition = margin;
  const fontSize = 11; // 11pt font size
  const lineHeight = fontSize * 1.2; // 1.2 line spacing
  const paragraphSpacing = fontSize * 1.2;

  // Colors
  const mainBlue = '#00AAFF';
  const deepBlue = '#1B4C87';
  const lightGray = '#D9D9D9';

  // Helper function to add text with word wrapping
  const addText = (text, x, y, options = {}) => {
    const currentFontSize = options.fontSize || fontSize;
    const fontStyle = options.fontStyle || 'normal';
    const color = options.color || '#000000';
    const maxWidth = options.maxWidth || contentWidth;
    const align = options.align || 'left';
    
    pdf.setFontSize(currentFontSize);
    pdf.setFont('helvetica', fontStyle);
    pdf.setTextColor(color);
    
    const lines = pdf.splitTextToSize(text, maxWidth);
    
    if (align === 'center') {
      x = pageWidth / 2;
      pdf.text(lines, x, y, { align: 'center' });
    } else if (align === 'right') {
      x = pageWidth - margin;
      pdf.text(lines, x, y, { align: 'right' });
    } else {
      pdf.text(lines, x, y);
    }
    
    return y + (lines.length * lineHeight);
  };

  // Helper function to add clickable link
  const addLink = (text, url, x, y, options = {}) => {
    const currentFontSize = options.fontSize || fontSize;
    const fontStyle = options.fontStyle || 'normal';
    const maxWidth = options.maxWidth || contentWidth;
    
    pdf.setFontSize(currentFontSize);
    pdf.setFont('helvetica', fontStyle);
    pdf.setTextColor(mainBlue);
    
    const lines = pdf.splitTextToSize(text, maxWidth);
    const textWidth = pdf.getTextWidth(lines[0]);
    
    // Add the text
    pdf.text(lines[0], x, y);
    
    // Create the link rectangle
    pdf.link(x, y - 10, textWidth, 12, { url: url });
    
    return y + (lines.length * lineHeight);
  };

  // Add logo image
  try {
    const logoUrl = 'https://media.publit.io/file/ProsperityLeaders/Prosperity-Elephant-WIDE-BLUEPNG.png';
    pdf.addImage(logoUrl, 'PNG', margin, yPosition, 120, 40); // 40px height, auto width
  } catch (error) {
    console.warn('Logo could not be loaded:', error);
  }

  // Header text - positioned next to logo
  const headerTextX = margin + 135; // After logo
  yPosition = addText('Prosperity Leaders™', headerTextX, yPosition + 10, {
    fontSize: 20,
    fontStyle: 'bold',
    color: deepBlue
  });
  
  yPosition = addText('Enterprise Corp.', headerTextX, yPosition + 2, {
    fontSize: 12,
    color: mainBlue
  });
  
  yPosition = addText('Financial Education & Community Outreach', headerTextX, yPosition + 2, {
    fontSize: 10,
    color: '#666666'
  });

  // Updated Blue Divider Line - 90% width, left-aligned
  yPosition += 15; // 6-8px spacing before line
  pdf.setDrawColor(mainBlue);
  pdf.setLineWidth(3);
  pdf.line(margin, yPosition, margin + (contentWidth * 0.9), yPosition);
  yPosition += 30; // Space after line

  // Date
  const template = letterTemplates[formData.language];
  const formattedDate = new Date(formData.date).toLocaleDateString(
    formData.language === 'spanish' ? 'es-ES' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' }
  );
  
  yPosition = addText(`${template.dateLabel}: ${formattedDate}`, pageWidth - margin, yPosition, {
    align: 'right',
    fontStyle: 'bold'
  });
  
  yPosition += paragraphSpacing * 2;

  // Greeting
  yPosition = addText(`${template.greeting} ${formData.recipientName}:`, margin, yPosition, {
    fontStyle: 'bold'
  });
  
  yPosition += paragraphSpacing;

  // Process template content
  const processTemplate = (text) => {
    return text
      .replace(/{{fecha}}/g, formattedDate)
      .replace(/{{recipient_name}}/g, formData.recipientName)
      .replace(/{{agent_name}}/g, formData.agentName)
      .replace(/{{office}}/g, formData.office)
      .replace(/{{city}}/g, formData.city)
      .replace(/{{agent_title}}/g, formData.agentTitle || 'Prosperity Leaders™ Representative')
      .replace(/{{additional_notes}}/g, formData.additionalNotes || '');
  };

  // Main content
  const contentParagraphs = processTemplate(template.content).split('\n\n');
  
  contentParagraphs.forEach(paragraph => {
    if (paragraph.trim()) {
      yPosition = addText(paragraph.trim(), margin, yPosition, {
        maxWidth: contentWidth
      });
      yPosition += paragraphSpacing;
    }
  });

  // Additional notes if provided
  if (formData.additionalNotes) {
    yPosition += paragraphSpacing;
    yPosition = addText(formData.additionalNotes, margin, yPosition, {
      maxWidth: contentWidth,
      color: '#444444'
    });
    yPosition += paragraphSpacing;
  }

  yPosition += paragraphSpacing;

  // Closing
  yPosition = addText(template.closing, margin, yPosition);
  yPosition += paragraphSpacing * 2;

  // Updated Signature Order - Representative First
  // Agent signature line
  pdf.setDrawColor('#000000');
  pdf.setLineWidth(1);
  pdf.line(margin, yPosition, margin + 250, yPosition);
  
  yPosition += 15;
  yPosition = addText(`${formData.agentName}, ${formData.agentTitle || 'Prosperity Leaders™ Representative'}`, margin, yPosition);
  yPosition = addText('Prosperity Leaders™ Representative', margin, yPosition, {
    fontSize: 10
  });

  // Founders signature
  yPosition += paragraphSpacing * 2;
  yPosition = addText('Jenny & Sebastian Rodriguez', margin, yPosition, {
    fontStyle: 'bold'
  });
  
  yPosition = addText(
    formData.language === 'spanish' 
      ? 'Fundadores; Prosperity Leaders Enterprise Corp.'
      : 'Founders; Prosperity Leaders Enterprise Corp.', 
    margin, 
    yPosition, 
    {
      fontSize: 10,
      fontStyle: 'italic'
    }
  );

  // Check if we need a new page for footer content
  if (yPosition > pageHeight - 150) {
    pdf.addPage();
    yPosition = margin;
  } else {
    yPosition += paragraphSpacing * 2;
  }

  // Updated Institution Information - Plain text rendering
  pdf.setDrawColor(lightGray);
  pdf.setLineWidth(1);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 20;

  // Render the recognition section as plain text with proper formatting
  const recognitionLines = template.recognition.split('\n');
  
  recognitionLines.forEach(line => {
    if (line.trim()) {
      if (line.startsWith('•')) {
        // Bullet point with indentation
        yPosition = addText(line.trim(), margin + 20, yPosition, {
          fontSize: fontSize,
          maxWidth: contentWidth - 20
        });
      } else {
        // Regular text
        yPosition = addText(line.trim(), margin, yPosition, {
          fontSize: fontSize,
          maxWidth: contentWidth
        });
      }
      yPosition += paragraphSpacing / 2;
    }
  });

  // Updated Footer with Clickable Links
  yPosition = pageHeight - margin - 40; // 0.75in from bottom
  pdf.setDrawColor(lightGray);
  pdf.setLineWidth(1);
  pdf.line(margin, yPosition - 20, pageWidth - margin, yPosition - 20);

  yPosition = addText('Prosperity Leaders Enterprise Corp.', pageWidth / 2, yPosition, {
    fontSize: 10,
    fontStyle: 'bold',
    align: 'center'
  });
  
  yPosition = addText('632 Newark Avenue, Suite 212, Jersey City, NJ 07306', pageWidth / 2, yPosition, {
    fontSize: 9,
    align: 'center'
  });
  
  yPosition = addText('P: 551-325-3211', pageWidth / 2, yPosition, {
    fontSize: 9,
    align: 'center'
  });
  
  // Add clickable links
  yPosition += 5;
  const linkX = (pageWidth / 2) - 80; // Center the links
  
  yPosition = addLink('ProsperityLeaders.net', 'https://prosperityleaders.net', linkX, yPosition, {
    fontSize: 9,
    align: 'left'
  });
  
  // Add the separator
  pdf.text(' | ', linkX + 75, yPosition - 3);
  
  yPosition = addLink('Bloomberg Businessweek', 'http://links.prosperityleaders.net/bloomberg', linkX + 85, yPosition - lineHeight, {
    fontSize: 9,
    align: 'left'
  });

  // Generate filename
  const sanitizeName = (name) => name.replace(/[^a-zA-Z0-9]/g, '-');
  const filename = `Community-Letter-${sanitizeName(formData.agentName)}-${sanitizeName(formData.office)}-${formData.date}.pdf`;

  // Download the PDF
  pdf.save(filename);
};